import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * Hook that uses event delegation to capture clicks on images
 * rendered via dangerouslySetInnerHTML (from renderContent()).
 *
 * Filters out math formula images and tiny decorative images.
 *
 * @returns {{ lightboxState, openLightbox, closeLightbox, containerRef }}
 */
export function useImageLightbox(contentKey) {
  const containerRef = useRef(null)
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    src: null,
    alt: null
  })

  const openLightbox = useCallback((src, alt) => {
    setLightboxState({ isOpen: true, src, alt })
  }, [])

  const closeLightbox = useCallback(() => {
    setLightboxState({ isOpen: false, src: null, alt: null })
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const svgToDataUri = (svgEl) => {
      const clone = svgEl.cloneNode(true)
      if (!clone.getAttribute('width') || !clone.getAttribute('height')) {
        const vb = clone.getAttribute('viewBox')
        if (vb) {
          const parts = vb.split(/[\s,]+/)
          if (!clone.getAttribute('width')) clone.setAttribute('width', parts[2])
          if (!clone.getAttribute('height')) clone.setAttribute('height', parts[3])
        }
      }
      const serializer = new XMLSerializer()
      const svgString = serializer.serializeToString(clone)
      return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString)
    }

    const handleClick = (e) => {
      // Check for img click
      const img = e.target.closest('img')
      if (img) {
        if (img.getAttribute('role') === 'math') return
        if (img.classList.contains('math-img')) return
        if (img.naturalWidth > 0 && img.naturalWidth < 50) return
        const src = img.src || img.getAttribute('src')
        if (!src) return
        e.preventDefault()
        openLightbox(src, img.alt || '')
        return
      }

      // Check for inline SVG click
      const svg = e.target.closest('svg')
      if (svg && container.contains(svg)) {
        if (svg.closest('.img-expand-icon')) return
        if (svg.getAttribute('role') === 'math') return
        if (svg.classList.contains('math-img')) return
        const w = parseFloat(svg.getAttribute('width'))
        const h = parseFloat(svg.getAttribute('height'))
        if (w && h && w < 50 && h < 50) return
        e.preventDefault()
        openLightbox(svgToDataUri(svg), 'SVG image')
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [openLightbox, contentKey])

  // Inject expand icon overlays on qualifying images
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const EXPAND_SVG =
      '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>'

    const wrappedImages = []

    const wrapImage = (img) => {
      // Skip math images
      if (img.getAttribute('role') === 'math') return
      if (img.classList.contains('math-img')) return
      // Skip tiny/decorative images
      if (img.naturalWidth > 0 && img.naturalWidth < 50) return
      // Skip already wrapped
      if (img.parentElement?.classList.contains('img-lightbox-wrapper')) return

      const wrapper = document.createElement('span')
      wrapper.className = 'img-lightbox-wrapper'

      const icon = document.createElement('span')
      icon.className = 'img-expand-icon'
      icon.innerHTML = EXPAND_SVG

      img.parentNode.insertBefore(wrapper, img)
      wrapper.appendChild(img)
      wrapper.appendChild(icon)
      wrappedImages.push({ img, wrapper })
    }

    const wrapSvg = (svg) => {
      if (svg.getAttribute('role') === 'math') return
      if (svg.classList.contains('math-img')) return
      // Skip expand icon SVGs injected by this hook
      if (svg.closest('.img-expand-icon')) return
      const w = parseFloat(svg.getAttribute('width'))
      const h = parseFloat(svg.getAttribute('height'))
      if (w && h && w < 50 && h < 50) return
      if (svg.parentElement?.classList.contains('img-lightbox-wrapper')) return

      const wrapper = document.createElement('span')
      wrapper.className = 'img-lightbox-wrapper'

      const icon = document.createElement('span')
      icon.className = 'img-expand-icon'
      icon.innerHTML = EXPAND_SVG

      svg.parentNode.insertBefore(wrapper, svg)
      wrapper.appendChild(svg)
      wrapper.appendChild(icon)
      wrappedImages.push({ img: svg, wrapper })
    }

    const processImages = () => {
      const imgs = container.querySelectorAll('img')
      imgs.forEach((img) => {
        if (img.complete) {
          wrapImage(img)
        } else {
          img.addEventListener('load', () => wrapImage(img), { once: true })
        }
      })

      // Also wrap inline SVGs (e.g. matplotlib plots)
      const svgs = container.querySelectorAll('svg')
      svgs.forEach((svg) => wrapSvg(svg))
    }

    // Process after a short delay to let dangerouslySetInnerHTML render
    const timer = setTimeout(processImages, 100)

    return () => {
      clearTimeout(timer)
      // Unwrap images on cleanup
      wrappedImages.forEach(({ img, wrapper }) => {
        if (wrapper.parentNode) {
          wrapper.parentNode.insertBefore(img, wrapper)
          wrapper.remove()
        }
      })
    }
  }, [lightboxState.isOpen, contentKey]) // Re-run when lightbox closes or content changes

  return { lightboxState, openLightbox, closeLightbox, containerRef }
}

export default useImageLightbox
